import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'wouter';
import { RefreshCw, ExternalLink, Search, Calendar, DollarSign, Building2, Tag, ChevronUp, ChevronDown, ChevronsUpDown, Sparkles, ClipboardCopy, Check, ArrowLeft, X } from 'lucide-react';
import { customInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';

interface Grant {
  id: string;
  source: string;
  state: string | null;
  name: string;
  funder: string;
  status: string;
  openDate: string | null;
  closeDate: string | null;
  fundingMin: string | null;
  fundingMax: string | null;
  eligibility: string | null;
  evidenceRequired: string | null;
  description: string | null;
  applicationUrl: string | null;
  industry: string | null;
  tags: string[] | null;
  lastSyncedAt: string;
}

interface AidAnswer {
  criterion: string;
  answer: string;
  sourceData?: string[];
}

interface ApplicationAid {
  id: string;
  grantId: string;
  answers: AidAnswer[];
  generatedAt: string;
  orgProfileVersion: string | null;
}

type SortKey = 'name' | 'funder' | 'status' | 'fundingMax' | 'closeDate';
type SortDir = 'asc' | 'desc';

// Compute effective status: if the close date has passed and status is 'open', treat as 'closed'
function effectiveStatus(g: Grant): string {
  if (g.status === 'open' && g.closeDate && new Date(g.closeDate) < new Date()) return 'closed';
  return g.status;
}

const STATUS_FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'all', label: 'All' },
];

const INDUSTRY_COLOURS: Record<string, string> = {
  Technology: 'bg-blue-50 text-blue-700',
  Biotech: 'bg-purple-50 text-purple-700',
  Manufacturing: 'bg-orange-50 text-orange-700',
  'Clean Energy': 'bg-teal-50 text-teal-700',
  Agriculture: 'bg-lime-50 text-lime-700',
  Defence: 'bg-slate-100 text-slate-700',
  Export: 'bg-cyan-50 text-cyan-700',
  Research: 'bg-indigo-50 text-indigo-700',
  'Creative Industries': 'bg-pink-50 text-pink-700',
  General: 'bg-muted text-muted-foreground',
};


function formatCurrency(val: string | null): string | null {
  if (!val) return null;
  const n = Number(val);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString('en-AU')}`;
}

function SortIcon({ sortKey: activeKey, currentCol, sortDir }: { sortKey: SortKey; currentCol: SortKey; sortDir: SortDir }) {
  if (currentCol !== activeKey) return <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />;
  return sortDir === 'asc'
    ? <ChevronUp className="h-3 w-3" aria-hidden="true" />
    : <ChevronDown className="h-3 w-3" aria-hidden="true" />;
}

function DateTimeline({ openDate, closeDate }: { openDate: string | null; closeDate: string | null }) {
  if (!openDate && !closeDate) return null;
  const now = Date.now();
  const open = openDate ? new Date(openDate).getTime() : null;
  const close = closeDate ? new Date(closeDate).getTime() : null;
  let progress = 0;
  if (open && close && close > open) {
    progress = Math.min(100, Math.max(0, ((now - open) / (close - open)) * 100));
  } else if (open && now >= open) {
    progress = 50;
  }
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-2">
        <Calendar className="h-3.5 w-3.5" aria-hidden="true" /> Timeline
      </p>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
      </div>
      <div className="flex justify-between mt-2 text-xs text-muted-foreground">
        <span>{openDate ? new Date(openDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Open ongoing'}</span>
        <span>{closeDate ? new Date(closeDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No close date'}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prep Package Panel
// ---------------------------------------------------------------------------

function useCopyButton(text: string) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return { copied, copy };
}

function CopyButton({ text, label = 'Copy', size = 'sm' }: { text: string; label?: string; size?: 'sm' | 'xs' }) {
  const { copied, copy } = useCopyButton(text);
  return (
    <Button
      variant="ghost"
      size={size === 'xs' ? 'sm' : 'sm'}
      className={`gap-2 ${size === 'xs' ? 'text-xs h-6 px-2' : 'text-xs h-7 px-3'}`}
      onClick={copy}
    >
      {copied
        ? <><Check className="h-3 w-3 text-green-600" aria-hidden="true" /><span className="text-green-600">Copied</span></>
        : <><ClipboardCopy className="h-3 w-3" aria-hidden="true" />{label}</>}
    </Button>
  );
}

function PrepPackagePanel({
  grant,
  aid,
  generating,
  onBack,
  onGenerate,
}: {
  grant: Grant;
  aid: ApplicationAid | null;
  generating: boolean;
  onBack: () => void;
  onGenerate: () => void;
}) {
  const allAnswersText = aid
    ? aid.answers.map((a) => `${a.criterion}\n${a.answer}`).join('\n\n')
    : '';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b shrink-0">
        <button
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Back to grant details"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">Application Prep Package</p>
          <p className="text-sm font-semibold truncate">{grant.name}</p>
        </div>
        {aid && <CopyButton text={allAnswersText} label="Copy all" />}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {generating && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Sparkles className="h-8 w-8 text-primary animate-pulse" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">Analysing grant criteria and your R&amp;D data…</p>
          </div>
        )}

        {!generating && !aid && (
          <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
            <Sparkles className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium mb-1">Generate your prep package</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Claude will draft answers to this grant's key criteria using your organisation profile and R&D project data.
                Review and personalise before submitting.
              </p>
            </div>
            <Button onClick={onGenerate} size="sm" className="gap-2">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Generate Prep Package
            </Button>
            <Link href="/settings/grants" className="text-xs text-muted-foreground underline hover:no-underline">
              Set up your org profile first →
            </Link>
          </div>
        )}

        {!generating && aid && aid.answers.map((a, i) => (
          <div key={i} className="rounded-lg border bg-background overflow-hidden">
            <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground leading-snug">{a.criterion}</p>
              <CopyButton text={a.answer} size="xs" />
            </div>
            <p className="px-3 pb-3 text-sm leading-relaxed">{a.answer}</p>
            {Array.isArray(a.sourceData) && a.sourceData.length > 0 && (
              <div className="px-3 pb-3 border-t pt-2 bg-muted/20">
                <p className="text-xs font-medium text-muted-foreground mb-2">Evidence to reference:</p>
                <ul className="space-y-1">
                  {a.sourceData.map((s, j) => (
                    <li key={j} className="text-xs text-muted-foreground flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" aria-hidden="true" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}

        {!generating && aid && (
          <p className="text-xs text-muted-foreground/50 text-center pt-2">
            Generated {new Date(aid.generatedAt).toLocaleDateString('en-AU')} ·
            Review and personalise before submitting
          </p>
        )}
      </div>

      {/* Footer — portal link */}
      {grant.applicationUrl && (
        <div className="p-4 border-t space-y-2 shrink-0">
          {!generating && aid && (
            <p className="text-xs text-muted-foreground text-center mb-1">
              Copy your answers above, then open the portal to paste them in.
            </p>
          )}
          <a
            href={grant.applicationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Open Application Portal <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </div>
      )}
    </div>
  );
}

function GrantDetailPanel({ grant, onClose }: { grant: Grant; onClose: () => void }) {
  const tags = Array.isArray(grant.tags) ? grant.tags : [];
  const industryStyle = grant.industry ? (INDUSTRY_COLOURS[grant.industry] ?? 'bg-muted text-muted-foreground') : null;
  const effStatus = effectiveStatus(grant);

  const [aid, setAid] = useState<ApplicationAid | null>(null);
  const [aidLoading, setAidLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showPrepPackage, setShowPrepPackage] = useState(false);

  // Close on ESC key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await customInstance<{ data: ApplicationAid }>({
          url: `/api/grants/${grant.id}/application-aid`,
          method: 'GET',
        });
        if (!cancelled) setAid(res.data);
      } catch {
        // 404 expected — no aid yet
      } finally {
        if (!cancelled) setAidLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [grant.id]);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await customInstance<{ data: ApplicationAid }>({
        url: `/api/grants/${grant.id}/application-aid`,
        method: 'POST',
      });
      setAid(res.data);
    } finally {
      setGenerating(false);
    }
  }, [grant.id]);

  const handleApplyNow = () => {
    setShowPrepPackage(true);
    if (!aid && !generating) {
      void generate();
    }
  };

  const daysUntilClose = grant.closeDate
    ? Math.ceil((new Date(grant.closeDate).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className="h-full flex flex-col">
      {showPrepPackage ? (
        <PrepPackagePanel
          grant={grant}
          aid={aid}
          generating={generating}
          onBack={() => setShowPrepPackage(false)}
          onGenerate={() => void generate()}
        />
      ) : (
        <>
          <div className="flex items-start justify-between p-5 border-b gap-3 shrink-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge
                  variant={effStatus === 'open' ? 'positive' : effStatus === 'forecast' ? 'warning' : 'default'}
                  className="capitalize"
                >
                  {effStatus}
                </Badge>
                {grant.state && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                    {grant.state}
                  </span>
                )}
                {grant.industry && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${industryStyle}`}>
                    {grant.industry}
                  </span>
                )}
              </div>
              <h2 className="font-semibold text-sm leading-snug">{grant.name}</h2>
              {grant.applicationUrl && (
                <a
                  href={grant.applicationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-xs text-primary hover:underline"
                >
                  View on {grant.source === 'business_gov' ? 'business.gov.au' : grant.source === 'grant_connect' ? 'GrantConnect' : grant.source.replace(/_/g, ' ')}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              )}
            </div>
            <button
              onClick={onClose}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors mt-1"
              aria-label="Close grant panel (Esc)"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Quick-facts */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border bg-muted/30 px-3 py-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                  <Building2 className="h-3.5 w-3.5" aria-hidden="true" /> Funder
                </p>
                <p className="text-sm font-medium leading-tight">{grant.funder}</p>
              </div>
              {(grant.fundingMin || grant.fundingMax) && (
                <div className="rounded-lg border bg-muted/30 px-3 py-3">
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <DollarSign className="h-3.5 w-3.5" aria-hidden="true" /> Funding
                  </p>
                  <p className="text-sm font-medium leading-tight">
                    {grant.fundingMax
                      ? `up to ${formatCurrency(grant.fundingMax)}`
                      : `from ${formatCurrency(grant.fundingMin!)}`}
                  </p>
                </div>
              )}
              {daysUntilClose !== null && (
                <div className={`col-span-2 rounded-lg border px-3 py-3 ${
                  daysUntilClose <= 0 ? 'bg-red-50 border-red-200'
                  : daysUntilClose <= 14 ? 'bg-red-50 border-red-200'
                  : daysUntilClose <= 30 ? 'bg-amber-50 border-amber-200'
                  : 'bg-muted/30'
                }`}>
                  <p className={`text-xs flex items-center gap-1 mb-1 ${
                    daysUntilClose <= 14 ? 'text-red-600' : daysUntilClose <= 30 ? 'text-amber-600' : 'text-muted-foreground'
                  }`}>
                    <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                    {daysUntilClose <= 0 ? 'Closed' : 'Closes'}
                  </p>
                  <p className={`text-sm font-medium ${daysUntilClose <= 14 ? 'text-red-700' : daysUntilClose <= 30 ? 'text-amber-700' : ''}`}>
                    {daysUntilClose <= 0
                      ? new Date(grant.closeDate!).toLocaleDateString('en-AU')
                      : `${daysUntilClose} day${daysUntilClose === 1 ? '' : 's'} — ${new Date(grant.closeDate!).toLocaleDateString('en-AU')}`}
                  </p>
                </div>
              )}
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5" aria-hidden="true" /> Tags
                </p>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-full text-xs border bg-background">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            <DateTimeline openDate={grant.openDate} closeDate={grant.closeDate} />

            {grant.description && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">About</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{grant.description}</p>
              </div>
            )}

            {grant.eligibility && grant.eligibility !== grant.description && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Eligibility</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{grant.eligibility}</p>
              </div>
            )}

            {grant.evidenceRequired && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Evidence required</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{grant.evidenceRequired}</p>
              </div>
            )}

            {/* Application Aid preview */}
            {!aidLoading && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    Application Aid
                  </p>
                  {aid && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6 px-2 gap-1"
                      onClick={() => void generate()}
                      disabled={generating}
                    >
                      <RefreshCw className={`h-3 w-3 ${generating ? 'animate-spin' : ''}`} aria-hidden="true" />
                      Regenerate
                    </Button>
                  )}
                </div>
                {aid
                  ? <p className="text-xs text-muted-foreground">Prep package ready · {aid.answers.length} criteria covered · Generated {new Date(aid.generatedAt).toLocaleDateString('en-AU')}</p>
                  : <p className="text-xs text-muted-foreground">Click "Prepare &amp; Apply" to generate Claude-drafted answers for this grant's criteria.</p>
                }
              </div>
            )}

            <p className="text-xs text-muted-foreground/50">
              Source: {grant.source.replace(/_/g, ' ')}{grant.state ? ` · ${grant.state}` : ''} · Last synced {new Date(grant.lastSyncedAt).toLocaleDateString('en-AU')}
            </p>
          </div>

          {grant.applicationUrl && (
            <div className="p-4 border-t shrink-0">
              <Button
                className="w-full gap-2"
                onClick={handleApplyNow}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {aid ? 'Apply Now — View Prep Package' : 'Prepare & Apply'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Panel width in px — kept in sync between the fixed panel and the main content margin
const PANEL_WIDTH = 480;

export function GrantsPage() {
  const [grantList, setGrantList] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [stateFilter, setStateFilter] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState<Grant | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('closeDate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const closeHandler = useRef<(() => void) | null>(null);

  const handleClose = useCallback(() => setSelected(null), []);
  closeHandler.current = handleClose;

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await customInstance<{ data: { items: Grant[] } }>({ url: '/api/grants', method: 'GET' });
      setGrantList(res.data.items);
    } catch {
      setError('Failed to load grants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await customInstance({ url: '/api/grants/sync', method: 'POST' });
      setTimeout(() => { void load(); }, 3000);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleSort = (col: SortKey) => {
    if (col === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let result = grantList.map((g) => ({ ...g, _effStatus: effectiveStatus(g) }));

    if (statusFilter !== 'all') result = result.filter((g) => g._effStatus === statusFilter);

    if (stateFilter === 'federal') result = result.filter((g) => !g.state);
    else if (stateFilter) result = result.filter((g) => g.state === stateFilter);

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((g) => {
        const tags = Array.isArray(g.tags) ? g.tags : [];
        return (
          g.name.toLowerCase().includes(q) ||
          g.funder.toLowerCase().includes(q) ||
          (g.description?.toLowerCase().includes(q) ?? false) ||
          (g.eligibility?.toLowerCase().includes(q) ?? false) ||
          (g.state?.toLowerCase().includes(q) ?? false) ||
          tags.some((t) => t.toLowerCase().includes(q))
        );
      });
    }

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':   cmp = a.name.localeCompare(b.name); break;
        case 'funder': cmp = a.funder.localeCompare(b.funder); break;
        case 'status': cmp = a._effStatus.localeCompare(b._effStatus); break;
        case 'fundingMax': {
          const av = a.fundingMax ? Number(a.fundingMax) : -1;
          const bv = b.fundingMax ? Number(b.fundingMax) : -1;
          cmp = av - bv;
          break;
        }
        case 'closeDate': {
          if (!a.closeDate && !b.closeDate) { cmp = 0; break; }
          if (!a.closeDate) { cmp = 1; break; }
          if (!b.closeDate) { cmp = -1; break; }
          cmp = new Date(a.closeDate).getTime() - new Date(b.closeDate).getTime();
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [grantList, statusFilter, stateFilter, searchQuery, sortKey, sortDir]);

  const thClass = (_col: SortKey) =>
    `text-left px-4 py-3 font-medium text-muted-foreground text-xs cursor-pointer select-none hover:text-foreground transition-colors`;

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  if (error) {
    return <InlineError message={error} onRetry={() => void load()} />;
  }

  return (
    <>
      {/* Main content area — shrinks right when panel is open */}
      <div
        className="space-y-6 transition-[padding] duration-300 ease-in-out"
        style={selected ? { paddingRight: `${PANEL_WIDTH + 24}px` } : undefined}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Grants</h1>
            <p className="text-sm text-muted-foreground mt-1">Australian R&D grants and funding programs</p>
            {grantList.length > 0 ? (() => {
              const latestSync = Math.max(...grantList.map((g) => new Date(g.lastSyncedAt).getTime()));
              return (
                <p className="text-xs text-muted-foreground mt-1">
                  Last synced: {new Date(latestSync).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(latestSync).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                </p>
              );
            })() : (
              <p className="text-xs text-muted-foreground mt-1">Never synced</p>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void triggerSync()}
            disabled={syncing}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
        </div>

        {/* Search + status chips */}
        <div className="space-y-3 mb-5">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <label htmlFor="grants-search" className="sr-only">Search grants</label>
              <input
                id="grants-search"
                type="text"
                placeholder="Search grants by name, funder, state, industry…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border bg-background"
              />
            </div>
            <Link href="/settings/grants" className="text-xs text-muted-foreground hover:underline shrink-0">Manage sources</Link>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <FilterChipGroup chips={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              aria-label="Filter by state"
              className="rounded-lg border bg-background px-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All jurisdictions</option>
              <option value="federal">Federal only</option>
              <option value="NSW">NSW</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="WA">WA</option>
              <option value="SA">SA</option>
              <option value="ACT">ACT</option>
              <option value="NT">NT</option>
              <option value="TAS">TAS</option>
            </select>
            {(statusFilter !== 'all' || stateFilter || searchQuery) && (
              <button
                onClick={() => { setStatusFilter('all'); setStateFilter(''); setSearchQuery(''); }}
                className="text-xs text-muted-foreground underline"
              >
                Clear
              </button>
            )}
            <span className="ml-auto text-xs text-muted-foreground">{filtered.length} grant{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Search className="h-8 w-8" />}
            title={grantList.length === 0 ? 'No grants found' : statusFilter === 'all' ? 'No grants match your filters' : `No ${statusFilter} grants`}
            body={grantList.length === 0
              ? 'Click "Sync Now" to discover Australian R&D grants.'
              : 'Try adjusting your filters or check back later for new opportunities.'}
          />
        ) : (
          <div className="max-h-[560px] overflow-y-auto rounded-[14px] border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className={thClass('name')} onClick={() => handleSort('name')}>
                    <span className="flex items-center gap-1">Grant <SortIcon currentCol="name" sortKey={sortKey} sortDir={sortDir} /></span>
                  </th>
                  <th className={`${thClass('funder')} hidden md:table-cell`} onClick={() => handleSort('funder')}>
                    <span className="flex items-center gap-1">Funder <SortIcon currentCol="funder" sortKey={sortKey} sortDir={sortDir} /></span>
                  </th>
                  <th className={thClass('status')} onClick={() => handleSort('status')}>
                    <span className="flex items-center gap-1">Status <SortIcon currentCol="status" sortKey={sortKey} sortDir={sortDir} /></span>
                  </th>
                  <th className={`${thClass('fundingMax')} hidden lg:table-cell`} onClick={() => handleSort('fundingMax')}>
                    <span className="flex items-center gap-1">Max funding <SortIcon currentCol="fundingMax" sortKey={sortKey} sortDir={sortDir} /></span>
                  </th>
                  <th className={`${thClass('closeDate')} hidden lg:table-cell`} onClick={() => handleSort('closeDate')}>
                    <span className="flex items-center gap-1">Closes <SortIcon currentCol="closeDate" sortKey={sortKey} sortDir={sortDir} /></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => {
                  const tags = Array.isArray(g.tags) ? g.tags : [];
                  const isSelected = selected?.id === g.id;
                  return (
                    <tr
                      key={g.id}
                      onClick={() => setSelected(isSelected ? null : g)}
                      className={`border-b last:border-0 cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-muted/30'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium leading-tight">{g.name}</p>
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          {g.state && (
                            <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">{g.state}</span>
                          )}
                          {g.industry && (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${INDUSTRY_COLOURS[g.industry] ?? 'bg-muted text-muted-foreground'}`}>
                              {g.industry}
                            </span>
                          )}
                        </div>
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {tags.slice(0, 3).map((t) => (
                              <span key={t} className="px-2 py-0.5 rounded text-xs border bg-background text-muted-foreground">{t}</span>
                            ))}
                            {tags.length > 3 && (
                              <span className="px-2 py-0.5 text-xs text-muted-foreground">+{tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{g.funder}</td>
                      <td className="px-4 py-3">
                        <Badge variant={g._effStatus === 'open' ? 'positive' : g._effStatus === 'forecast' ? 'warning' : 'default'} className="capitalize">
                          {g._effStatus}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                        {g.fundingMax ? formatCurrency(g.fundingMax) : '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {g.closeDate ? (
                          <span className={
                            g._effStatus === 'closed' ? 'text-muted-foreground/60' :
                            (() => {
                              const d = Math.ceil((new Date(g.closeDate).getTime() - Date.now()) / 86_400_000);
                              return d <= 14 ? 'text-red-600 font-medium' : d <= 30 ? 'text-amber-600' : 'text-muted-foreground';
                            })()
                          }>
                            {new Date(g.closeDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail panel — fixed to viewport right edge, full height */}
      {selected && (
        <div
          className="fixed inset-y-0 right-0 bg-card border-l shadow-xl z-50 flex flex-col"
          style={{ width: `${PANEL_WIDTH}px` }}
        >
          <GrantDetailPanel
            grant={selected}
            onClose={handleClose}
          />
        </div>
      )}
    </>
  );
}
